import React, { useState, useEffect, useRef } from 'react';
import {
  parseGS1Barcode,
  parseGS1BarcodeWithSegments,
  validateGTIN,
  formatGS1DataForDisplay,
  getAIDefinitions,
  getDefaultSeparators,
  BarcodeSegment
} from '../../../utils/gs1Parser';
import { Card } from '../Card';
import {
  Barcode,
  CheckCircle,
  XCircle,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  Copy,
  CheckCheck,
  Settings,
  RotateCcw,
  ScanLine
} from 'lucide-react';

interface GS1TestData {
  name: string;
  barcode: string;
  description: string;
}

const testBarcodes: GS1TestData[] = [
  {
    name: 'SSCC Palle-ID',
    barcode: '00376130321109876543',
    description: 'Serial Shipping Container Code (AI 00) - 18 siffer'
  },
  {
    name: 'SSCC + GTIN',
    barcode: '00376130321109876543010743210000115210BATCH123',
    description: 'SSCC kombinert med GTIN og batchnummer'
  },
  {
    name: 'Komplett eksempel',
    barcode: '010743210000115210BATCH~21SERIAL~37100~15241205',
    description: 'GTIN + Batch + Serial + Mengde + Best før (med ~ separator)'
  },
  {
    name: 'Kun GTIN',
    barcode: '01074321000011520',
    description: 'Bare Global Trade Item Number (gyldig checksum)'
  },
  {
    name: 'GTIN + Batch',
    barcode: '010743210000115210ABC123',
    description: 'GTIN og batchnummer (variabel lengde)'
  },
  {
    name: 'GTIN + Batch + Serial',
    barcode: '010743210000115210LOT456~21SER789',
    description: 'GTIN med batch og serienummer (~ separator)'
  },
  {
    name: 'Med serienummer',
    barcode: '01074321000011522112345678',
    description: 'GTIN og serienummer (variabel lengde)'
  },
  {
    name: 'Med vekt',
    barcode: '01074321000011523100001500',
    description: 'GTIN og netto vekt (1.500 kg)'
  },
  {
    name: 'Flere AI-koder',
    barcode: '010743210000115210BATCH~21SERIAL~37100~15241205',
    description: 'GTIN + Batch + Serial + Mengde + Best før (multi-AI med ~)'
  },
  {
    name: 'Kompleks med datoer',
    barcode: '010987654321098721SN123456~10LOT789~37250~15250630~17251231',
    description: 'Kompleks eksempel med flere datoer og variable felt'
  },
  {
    name: 'Med intern informasjon (AI 91)',
    barcode: '02273186904957213784~91PR22080003~',
    description: 'GTIN + AI 37 (Mengde) + AI 20 (Variant) + AI 91 (Intern info)'
  },
  {
    name: 'Pipe separator',
    barcode: '010743210000115210BATCH|21SERIAL|37100',
    description: 'Eksempel med pipe (|) som separator'
  },
  {
    name: 'Semikolon separator',
    barcode: '010743210000115210BATCH;21SERIAL;15241205',
    description: 'Eksempel med semikolon (;) som separator'
  }
];

const getAIColor = (ai: string): string => {
  if (ai === '00') return 'bg-teal-100 text-teal-800 border-teal-300';
  if (ai.startsWith('01') || ai.startsWith('02')) return 'bg-blue-100 text-blue-800 border-blue-300';
  if (ai.startsWith('10')) return 'bg-green-100 text-green-800 border-green-300';
  if (ai.startsWith('21')) return 'bg-purple-100 text-purple-800 border-purple-300';
  if (ai.startsWith('15') || ai.startsWith('17')) return 'bg-orange-100 text-orange-800 border-orange-300';
  if (ai.startsWith('37')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  if (ai.startsWith('31') || ai.startsWith('33')) return 'bg-pink-100 text-pink-800 border-pink-300';
  if (ai.startsWith('39')) return 'bg-indigo-100 text-indigo-800 border-indigo-300';
  if (ai.startsWith('9')) return 'bg-cyan-100 text-cyan-800 border-cyan-300';
  return 'bg-gray-100 text-gray-800 border-gray-300';
};

interface TestResult {
  id: string;
  barcode: string;
  parsed: any;
  segments: BarcodeSegment[];
  isValid: boolean;
  timestamp: Date;
  separators: string;
}

export const GS1TestComponent: React.FC = () => {
  const [testInput, setTestInput] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [showAIDefinitions, setShowAIDefinitions] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [separators, setSeparators] = useState<string>(getDefaultSeparators());
  const [showSeparatorConfig, setShowSeparatorConfig] = useState(true);

  // Global scanner state
  const [globalScannerEnabled, setGlobalScannerEnabled] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');

  // Refs for scanner logic
  const scanBufferRef = useRef<string>('');
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastKeystrokeTimeRef = useRef<number>(0);

  const handleParseBarcode = (barcode: string) => {
    if (!barcode.trim()) {
      return;
    }

    const parsed = parseGS1Barcode(barcode, separators);
    const segments = parseGS1BarcodeWithSegments(barcode, separators);

    const isValid = segments.length > 0;

    const result: TestResult = {
      id: Date.now().toString(),
      barcode: barcode,
      parsed: parsed,
      segments: segments,
      isValid: isValid,
      timestamp: new Date(),
      separators: separators
    };

    setTestResults(prev => [result, ...prev]);
  };

  // Process accumulated barcode from global scanner
  const processScannedBarcode = (barcode: string) => {
    if (!barcode.trim()) return;

    // Update the test input to show what was scanned
    setTestInput(barcode);
    // Parse the barcode
    handleParseBarcode(barcode);
    // Reset scanning state
    setIsScanning(false);
    setScanProgress('');
  };

  // Clear the scan buffer and reset state
  const clearScanBuffer = () => {
    scanBufferRef.current = '';
    setScanProgress('');
    setIsScanning(false);
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  };

  // Global keyboard event handler for scanner input
  useEffect(() => {
    if (!globalScannerEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea/select element
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' ||
                          target.tagName === 'TEXTAREA' ||
                          target.tagName === 'SELECT';

      // If focus is on an input field, let it handle the input normally
      if (isInputField) {
        return;
      }

      // Get the current timestamp
      const now = Date.now();
      const timeSinceLastKey = now - lastKeystrokeTimeRef.current;

      // Scanner typically sends characters very fast (< 50ms between chars)
      // If time between keystrokes is > 150ms, it's likely manual typing - reset buffer
      if (timeSinceLastKey > 150 && scanBufferRef.current.length > 0) {
        clearScanBuffer();
      }

      lastKeystrokeTimeRef.current = now;

      // Handle Enter key - process the accumulated barcode
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBufferRef.current.length > 0) {
          processScannedBarcode(scanBufferRef.current);
          clearScanBuffer();
        }
        return;
      }

      // Handle Escape key - cancel scanning
      if (event.key === 'Escape') {
        clearScanBuffer();
        return;
      }

      // Ignore modifier keys, function keys, navigation keys, etc.
      if (event.key.length > 1 &&
          event.key !== 'Enter' &&
          event.key !== 'Escape') {
        return;
      }

      // Accumulate printable characters
      if (event.key.length === 1) {
        event.preventDefault(); // Prevent default only for captured characters

        scanBufferRef.current += event.key;
        setIsScanning(true);
        setScanProgress(scanBufferRef.current);

        // Clear any existing timeout
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }

        // Set timeout to process barcode if no more input comes
        // Scanners typically finish in < 100ms, so 150ms timeout is safe
        scanTimeoutRef.current = setTimeout(() => {
          if (scanBufferRef.current.length > 0) {
            processScannedBarcode(scanBufferRef.current);
            clearScanBuffer();
          }
        }, 150);
      }
    };

    // Attach the event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [globalScannerEnabled, separators]);

  const handleTestBarcode = (barcode: string) => {
    setTestInput(barcode);
    handleParseBarcode(barcode);
  };

  const handleBatchTest = () => {
    if (!batchInput.trim()) return;
    
    const lines = batchInput.split('\n').filter(line => line.trim());
    
    lines.forEach(line => {
      handleParseBarcode(line.trim());
    });
    
    setBatchInput('');
  };

  const handleClearResults = () => {
    setTestResults([]);
  };

  const handleCopyBarcode = (barcode: string, id: string) => {
    navigator.clipboard.writeText(barcode);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleResetSeparators = () => {
    setSeparators(getDefaultSeparators());
  };

  const renderSeparatorDisplay = (sep: string): string => {
    const chars: string[] = [];
    for (let i = 0; i < sep.length; i++) {
      const code = sep.charCodeAt(i);
      if (code === 29) {
        chars.push('FNC1(ASCII 29)');
      } else if (code < 32) {
        chars.push(`ASCII ${code}`);
      } else {
        chars.push(sep[i]);
      }
    }
    return chars.join(', ');
  };

  const aiDefinitions = getAIDefinitions();

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Barcode className="text-blue-600" size={32} />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">GS1-128 Barcode Test</h2>
              <p className="text-sm text-gray-600">Test og massetest GS1-128 strekkoder med konfigurerbare separatorer</p>
            </div>
          </div>

          {/* Global Scanner Toggle */}
          <button
            onClick={() => setGlobalScannerEnabled(!globalScannerEnabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              globalScannerEnabled
                ? 'bg-green-100 text-green-800 hover:bg-green-200 border-2 border-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-gray-300'
            }`}
            title={globalScannerEnabled ? 'Klikk for å deaktivere global skanner' : 'Klikk for å aktivere global skanner'}
          >
            <CheckCircle className={globalScannerEnabled ? 'text-green-600' : 'text-gray-500'} size={18} />
            <span className="font-medium text-sm">
              Global Skanner: {globalScannerEnabled ? 'PÅ' : 'AV'}
            </span>
          </button>
        </div>

        {/* Scanner Status Indicator - Only shows during scanning */}
        {isScanning && (
          <div className="mb-4 p-3 rounded-lg border-2 bg-yellow-50 border-yellow-300 animate-pulse">
            <div className="flex items-center gap-2">
              <ScanLine className="text-yellow-600 animate-bounce" size={20} />
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800">Skanner...</p>
                <p className="text-xs text-yellow-700 font-mono mt-1 break-all">
                  {scanProgress}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Konfigurerbare separatorer</p>
              <p>Dette test-verktøyet støtter valgfrie separatorer for variable felt.
              Du kan angi flere separatorer som en streng (f.eks. '~|;').
              Standard er FNC1 (ASCII 29) + tilde (~).</p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="text-gray-700" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">Separator Konfigurasjon</h3>
          </div>
          <button
            onClick={() => setShowSeparatorConfig(!showSeparatorConfig)}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            {showSeparatorConfig ? <EyeOff size={16} /> : <Eye size={16} />}
            <span>{showSeparatorConfig ? 'Skjul' : 'Vis'}</span>
          </button>
        </div>

        {showSeparatorConfig && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Separator tegn (string)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={separators}
                  onChange={(e) => setSeparators(e.target.value)}
                  placeholder="Skriv separator-tegn, f.eks. ~|;"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                />
                <button
                  onClick={handleResetSeparators}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
                  title="Tilbakestill til standard"
                >
                  <RotateCcw size={16} />
                  Standard
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Nåværende separatorer: <span className="font-mono font-semibold">{renderSeparatorDisplay(separators)}</span>
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="text-yellow-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-xs text-yellow-800">
                  <p className="font-medium mb-1">Tips om separatorer:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Skriv flere tegn etter hverandre for å støtte flere separatorer (f.eks. '~|;')</li>
                    <li>Standard er FNC1 (ASCII 29) + tilde (~)</li>
                    <li>FNC1 (ASCII 29) vises vanligvis ikke i klienten, men kan brukes av skannere</li>
                    <li>Vanlige alternativer: tilde (~), pipe (|), semikolon (;)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Enkelt Test</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleParseBarcode(testInput);
              }
            }}
            placeholder="Skriv inn GS1-128 strekkode..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={() => handleParseBarcode(testInput)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Parse
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Massetest</h3>
        <p className="text-sm text-gray-600 mb-3">Lim inn flere strekkoder (en per linje)</p>
        <textarea
          value={batchInput}
          onChange={(e) => setBatchInput(e.target.value)}
          placeholder="00376130321109876543&#10;010743210000115210ABC123~171205031537000123&#10;01074321000011520&#10;010743210000115210LOT456~21SER789&#10;..."
          className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleBatchTest}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus size={16} />
            Parse alle
          </button>
          <button
            onClick={() => setBatchInput('')}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Tøm
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Eksempler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testBarcodes.map((test, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{test.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{test.description}</p>
                </div>
                <button
                  onClick={() => handleTestBarcode(test.barcode)}
                  className="ml-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  Test
                </button>
              </div>
              <code className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded block mt-2 break-all font-mono">
                {test.barcode}
              </code>
            </div>
          ))}
        </div>
      </Card>

      {testResults.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Test Resultater ({testResults.length})
            </h3>
            <button
              onClick={handleClearResults}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
            >
              <Trash2 size={16} />
              Tøm alle
            </button>
          </div>

          <div className="space-y-4">
            {testResults.map((result) => (
              <div 
                key={result.id} 
                className={`border-2 rounded-lg p-4 ${
                  result.isValid 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {result.isValid ? (
                        <CheckCircle className="text-green-600" size={20} />
                      ) : (
                        <XCircle className="text-red-600" size={20} />
                      )}
                      <span className="font-mono text-sm text-gray-700 break-all">
                        {result.barcode}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{result.timestamp.toLocaleTimeString('no-NO')}</span>
                      <span>|</span>
                      <span>Separatorer: {renderSeparatorDisplay(result.separators)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopyBarcode(result.barcode, result.id)}
                    className="ml-2 p-2 hover:bg-white rounded transition-colors"
                    title="Kopier strekkode"
                  >
                    {copiedId === result.id ? (
                      <CheckCheck className="text-green-600" size={16} />
                    ) : (
                      <Copy className="text-gray-600" size={16} />
                    )}
                  </button>
                </div>

                {result.segments.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Tolkede segmenter:</h4>
                    <div className="space-y-2">
                      {result.segments.map((segment, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center gap-2 p-2 rounded border ${getAIColor(segment.ai)}`}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <code className="font-bold text-sm">AI {segment.ai}</code>
                            <span className="text-xs opacity-75">|</span>
                            <code className="font-mono font-semibold">{segment.value}</code>
                            {segment.ai === '01' || segment.ai === '02' ? (
                              segment.isValid ? (
                                <CheckCircle size={14} className="text-green-600" />
                              ) : (
                                <XCircle size={14} className="text-red-600" />
                              )
                            ) : null}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-medium">{segment.description}</div>
                            <div className="text-xs opacity-75">{segment.dataType}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.parsed && Object.keys(result.parsed.parsedAIs || {}).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded p-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Formatert visning:</h4>
                    <div className="space-y-1 text-sm">
                      {result.parsed.sscc && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">SSCC:</span>
                          <span className="text-gray-900 font-mono">{result.parsed.sscc}</span>
                        </div>
                      )}
                      {result.parsed.gtin && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">GTIN:</span>
                          <span className="text-gray-900 font-mono">{result.parsed.gtin}</span>
                          {validateGTIN(result.parsed.gtin) ? (
                            <CheckCircle size={14} className="text-green-600" />
                          ) : (
                            <XCircle size={14} className="text-red-600" />
                          )}
                        </div>
                      )}
                      {result.parsed.batchNo && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Batch:</span>
                          <span className="text-gray-900 font-mono">{result.parsed.batchNo}</span>
                        </div>
                      )}
                      {result.parsed.serialNo && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Serial:</span>
                          <span className="text-gray-900 font-mono">{result.parsed.serialNo}</span>
                        </div>
                      )}
                      {result.parsed.quantity && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Mengde:</span>
                          <span className="text-gray-900">{result.parsed.quantity}</span>
                        </div>
                      )}
                      {result.parsed.bestBeforeDate && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Best før:</span>
                          <span className="text-gray-900">{result.parsed.bestBeforeDate}</span>
                        </div>
                      )}
                      {result.parsed.useByDate && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Bruk innen:</span>
                          <span className="text-gray-900">{result.parsed.useByDate}</span>
                        </div>
                      )}
                      {result.parsed.netWeight && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Netto vekt:</span>
                          <span className="text-gray-900">{result.parsed.netWeight} kg</span>
                        </div>
                      )}
                      {result.parsed.grossWeight && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Brutto vekt:</span>
                          <span className="text-gray-900">{result.parsed.grossWeight} kg</span>
                        </div>
                      )}
                      {result.parsed.internalInfo && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">Intern info:</span>
                          <span className="text-gray-900 font-mono">{result.parsed.internalInfo}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {result.segments.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                    Ingen GS1 AI-koder funnet i denne strekkoden
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">GS1 AI Definisjoner</h3>
          <button
            onClick={() => setShowAIDefinitions(!showAIDefinitions)}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            {showAIDefinitions ? <EyeOff size={16} /> : <Eye size={16} />}
            <span>{showAIDefinitions ? 'Skjul' : 'Vis'} definisioner</span>
          </button>
        </div>
        
        {showAIDefinitions && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    AI Kode
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Beskrivelse
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Lengde
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {aiDefinitions.map((ai, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <code className={`text-sm px-2 py-1 rounded border font-semibold ${getAIColor(ai.ai)}`}>
                        {ai.ai}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {ai.description}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {ai.length === -1 ? (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                          Variabel
                        </span>
                      ) : (
                        ai.length
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {ai.dataType}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Bruksinstruksjoner</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Global skanner (ANBEFALT):</strong> Når aktivert, kan du skanne strekkoder hvor som helst på siden uten å fokusere på et felt. Perfekt for nettbrett hvor tastaturet ellers ville dekket skjermen!</li>
              <li>• <strong>Separator konfigurasjon:</strong> Angi ønskede separator-tegn som en streng (f.eks. '~|;' for tilde, pipe og semikolon)</li>
              <li>• <strong>Enkelt test:</strong> Skriv inn eller lim en GS1-128 strekkode i feltet øverst</li>
              <li>• <strong>Massetest:</strong> Lim inn flere strekkoder (en per linje) for å teste mange samtidig</li>
              <li>• <strong>Fargekodet visning:</strong> Ulike AI-koder har ulike farger for enkel identifikasjon</li>
              <li>• <strong>GTIN validering:</strong> GTIN-koder (AI 01/02) valideres automatisk med checksum</li>
              <li>• <strong>SSCC støtte:</strong> AI 00 (Serial Shipping Container Code) er nå støttet med teal farge</li>
              <li>• <strong>Test eksempler:</strong> Klikk på testeksemplene for å se hvordan ulike koder tolkes</li>
              <li>• <strong>Standard separatorer:</strong> FNC1 (ASCII 29) + tilde (~) brukes hvis ikke annet spesifiseres</li>
              <li>• <strong>Visuell tilbakemelding:</strong> Se sanntids status når skanneren mottar data med gul pulserende indikator</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};
